import { useState } from 'react';
import { AdminPanel } from '../components/dashboard/AdminPanel';
import { CustomerSelection, Customer } from '../components/dashboard/CustomerSelection';

export type MetricType = 'CO2e' | 'Waste' | 'TreesSaved';

export interface DataPoint {
    id: string;
    name: string;
    co2e: number;
    waste: number;
    treesSaved: number;
    description: string;
    potentialCo2e?: number;
    potentialWaste?: number;
    potentialTreesSaved?: number;
    // Year-over-year data
    previousYear?: {
        co2e: number;
        waste: number;
        treesSaved: number;
    };
    currentYear?: {
        co2e: number;
        waste: number;
        treesSaved: number;
    };
}

const initialData: DataPoint[] = [
    {
        id: 'chep',
        name: 'Total CHEP Pallets',
        co2e: -1340,
        waste: -2412,
        treesSaved: 67,
        description: 'Emissions reduced through optimized pallet reuse and circular logistics'
    },
    {
        id: 'supplier',
        name: 'Supplier Integration',
        co2e: -828,
        waste: -2484,
        treesSaved: 41,
        description: 'Carbon savings from collaborative supplier network optimization'
    },
    {
        id: 'cycle',
        name: 'Cycle Time',
        co2e: -233,
        waste: -186.4,
        treesSaved: 12,
        description: 'Efficiency gains from reduced production cycle times'
    },
    {
        id: 'transport',
        name: 'Transport Collaboration',
        co2e: -1,
        waste: -1.2,
        treesSaved: 0.05,
        description: 'Marginal improvements from shared transportation initiatives'
    }
];

export default function App() {
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [customers, setCustomers] = useState<Customer[]>([
        {
            id: '1',
            name: "Joe's Donuts",
            industry: 'Food Service',
            addedDate: '2024-01-15',
        },
        {
            id: '2',
            name: 'Walmart Canada',
            industry: 'Retail',
            addedDate: '2024-02-20',
        },
        {
            id: '3',
            name: 'SpartanNash',
            industry: 'Food Distribution',
            addedDate: '2024-03-10',
        },
        {
            id: '4',
            name: 'Target Corporation',
            industry: 'Retail',
            addedDate: '2024-04-05',
        },
    ]);

    // Store data per customer
    const [customerDataMap, setCustomerDataMap] = useState<{
        [customerId: string]: {
            graphData: DataPoint[];
            certificates: any[];
            ceData: any;
            suppliers: any[];
        };
    }>({});

    const [data, setData] = useState<DataPoint[]>(initialData);
    const [certificates, setCertificates] = useState([
        {
            id: 1,
            title: 'CHEP Scope 3 Emissions Factor',
            description: 'Comprehensive emissions data for Scope 3 calculations',
            date: '2024'
        },
        {
            id: 2,
            title: 'Deforestation-Free Statement',
            description: 'Commitment to sustainable forestry practices',
            date: '2024'
        },
        {
            id: 3,
            title: 'Brambles Sustainability Review',
            description: 'Annual sustainability performance review',
            date: '2023'
        },
        {
            id: 4,
            title: 'CHEP Life Cycle Assessment - NA Pallet',
            description: 'Complete LCA for North American pallet operations',
            date: '2023'
        },
        {
            id: 5,
            title: 'Our Sustainability Value Proposition',
            description: 'Overview of CHEP\'s sustainability commitments',
            date: '2024'
        }
    ]);

    const [ceUniversityData, setCEUniversityData] = useState({
        caseStudies: [
            {
                id: 1,
                title: 'Case Study: Walmart Canada',
                description: 'Explore how Walmart Canada implemented circular economy practices with CHEP',
                category: 'Case Study'
            },
            {
                id: 2,
                title: 'Optimizing Supply Chain Efficiency: SpartanNash\'s Strategic Partnership with CHEP',
                description: 'Learn how SpartanNash optimized their supply chain through CHEP partnership',
                category: 'Case Study'
            }
        ],
        videos: [
            {
                id: 1,
                title: 'Introduction to Circular Economy',
                description: 'Learn the fundamentals of circular economy and its impact on sustainability',
                duration: '15 min',
                category: 'Circular Economy'
            }
        ],
        webinars: [
            {
                id: 1,
                title: 'Extended Producer Responsibility US Part 1',
                description: 'Understanding EPR regulations and requirements in the United States',
                category: 'EPR Webinar'
            },
            {
                id: 2,
                title: 'Extended Producer Responsibility US Part 2',
                description: 'Advanced EPR implementation strategies for US businesses',
                category: 'EPR Webinar'
            },
            {
                id: 3,
                title: 'Extended Producer Responsibility CA Part 1',
                description: 'Overview of EPR regulations in Canada',
                category: 'EPR Webinar'
            },
            {
                id: 4,
                title: 'Extended Producer Responsibility CA Part 2',
                description: 'Implementing EPR compliance in Canadian operations',
                category: 'EPR Webinar'
            }
        ]
    });

    const [suppliers, setSuppliers] = useState<any[]>([]);

    const handleUpdateGraphData = (newData: DataPoint[]) => {
        setData(newData);
    };

    const handleUpdateCertificates = (newCertificates: any[]) => {
        setCertificates(newCertificates);
    };

    const handleUpdateCEUniversity = (newCEData: any) => {
        setCEUniversityData(newCEData);
    };

    const handleUpdateSuppliers = (newSuppliers: any[]) => {
        setSuppliers(newSuppliers);
    };

    const handleSelectCustomer = (customer: Customer) => {
        setSelectedCustomer(customer);

        // Load customer data if exists, otherwise use initial data
        if (customerDataMap[customer.id]) {
            setData(customerDataMap[customer.id].graphData);
            setCertificates(customerDataMap[customer.id].certificates);
            setCEUniversityData(customerDataMap[customer.id].ceData);
            setSuppliers(customerDataMap[customer.id].suppliers || []);
        } else {
            // Initialize with default data for new customer
            setData(initialData);
            setCertificates([
                {
                    id: 1,
                    title: 'CHEP Scope 3 Emissions Factor',
                    description: 'Comprehensive emissions data for Scope 3 calculations',
                    date: '2024'
                },
                {
                    id: 2,
                    title: 'Deforestation-Free Statement',
                    description: 'Commitment to sustainable forestry practices',
                    date: '2024'
                },
                {
                    id: 3,
                    title: 'Brambles Sustainability Review',
                    description: 'Annual sustainability performance review',
                    date: '2023'
                },
                {
                    id: 4,
                    title: 'CHEP Life Cycle Assessment - NA Pallet',
                    description: 'Complete LCA for North American pallet operations',
                    date: '2023'
                },
                {
                    id: 5,
                    title: 'Our Sustainability Value Proposition',
                    description: 'Overview of CHEP\'s sustainability commitments',
                    date: '2024'
                }
            ]);
            setCEUniversityData({
                caseStudies: [
                    {
                        id: 1,
                        title: 'Case Study: Walmart Canada',
                        description: 'Explore how Walmart Canada implemented circular economy practices with CHEP',
                        category: 'Case Study'
                    },
                    {
                        id: 2,
                        title: 'Optimizing Supply Chain Efficiency: SpartanNash\'s Strategic Partnership with CHEP',
                        description: 'Learn how SpartanNash optimized their supply chain through CHEP partnership',
                        category: 'Case Study'
                    }
                ],
                videos: [
                    {
                        id: 1,
                        title: 'Introduction to Circular Economy',
                        description: 'Learn the fundamentals of circular economy and its impact on sustainability',
                        duration: '15 min',
                        category: 'Circular Economy'
                    }
                ],
                webinars: [
                    {
                        id: 1,
                        title: 'Extended Producer Responsibility US Part 1',
                        description: 'Understanding EPR regulations and requirements in the United States',
                        category: 'EPR Webinar'
                    },
                    {
                        id: 2,
                        title: 'Extended Producer Responsibility US Part 2',
                        description: 'Advanced EPR implementation strategies for US businesses',
                        category: 'EPR Webinar'
                    },
                    {
                        id: 3,
                        title: 'Extended Producer Responsibility CA Part 1',
                        description: 'Overview of EPR regulations in Canada',
                        category: 'EPR Webinar'
                    },
                    {
                        id: 4,
                        title: 'Extended Producer Responsibility CA Part 2',
                        description: 'Implementing EPR compliance in Canadian operations',
                        category: 'EPR Webinar'
                    }
                ]
            });
            setSuppliers([]);
        }
    };

    const handleBackToCustomers = () => {
        // Save current customer data before going back
        if (selectedCustomer) {
            setCustomerDataMap({
                ...customerDataMap,
                [selectedCustomer.id]: {
                    graphData: data,
                    certificates: certificates,
                    ceData: ceUniversityData,
                    suppliers: suppliers,
                },
            });
        }
        setSelectedCustomer(null);
    };

    const handleAddCustomer = (customer: Customer) => {
        setCustomers([...customers, customer]);
    };

    const handleDeleteCustomer = (customerId: string) => {
        setCustomers(customers.filter(c => c.id !== customerId));
        // Also remove customer data
        const newMap = { ...customerDataMap };
        delete newMap[customerId];
        setCustomerDataMap(newMap);
    };

    // Show customer selection screen if no customer is selected
    if (!selectedCustomer) {
        return (
            <CustomerSelection
                customers={customers}
                onSelectCustomer={handleSelectCustomer}
                onAddCustomer={handleAddCustomer}
                onDeleteCustomer={handleDeleteCustomer}
            />
        );
    }

    // Show admin panel for selected customer
    return (
        <AdminPanel
            onUpdateGraphData={handleUpdateGraphData}
            onUpdateCertificates={handleUpdateCertificates}
            onUpdateCEUniversity={handleUpdateCEUniversity}
            onUpdateSuppliers={handleUpdateSuppliers}
            currentGraphData={data}
            currentCertificates={certificates}
            currentCEData={ceUniversityData}
            currentSuppliers={suppliers}
            onBackToCustomers={handleBackToCustomers}
            selectedCustomer={selectedCustomer}
        />
    );
}
